import { useTranslation } from "react-i18next";
import { Close } from "@mui/icons-material";
import { Box, TextField } from "@mui/material";
import { useGenerateCustomMutation } from "../../../../../redux/slices/featuresApiSlice";
import { useFormikContext } from "formik";
import { useState } from "react";
import { useWebSocket } from "../WebSocketContext";
import { toast } from "react-toastify";
import { GenerationButton } from "../FeaturesTemplate/FeaturesFields/GenerationButton";
import ReactSelect from "react-select";
import { useGenerationErrorHandling } from "../../../../../shared/utils/errorHandling";
import { trackPlatformUsed } from "../../../../../shared/utils/trackers";
import { dispatch } from "../../../../../redux/store";
import { setStreaming } from "../../../../../redux/slices/authSlice";

const CustomModal = ({ editor, customData }) => {
  const { t } = useTranslation();
  const { setOpenCustomModal, bufferRef, cleanGeneratedContent } =
    useWebSocket();
  const [generateCustom, { isLoading }] = useGenerateCustomMutation();
  const handleGenerationError = useGenerationErrorHandling();
  const formikProps = useFormikContext();
  const [prompt, setPrompt] = useState("");

  const [includeGV, setIncludeGV] = useState(true);
  const [includeContext, setIncludeContext] = useState(false);
  const [websearch, setWebsearch] = useState(false);
  const [selectedModel, setSelectedModel] = useState("default");
  const models = [
    {
      name: "chatgpt-4o-latest",
      label: "GPT-4o (dernière version)",
      platform: "OpenAI",
    },
    {
      name: "gpt-4o",
      label: "GPT-4o",
      platform: "OpenAI",
    },
    {
      name: "gpt-4o mini",
      label: "GPT-4o mini",
      platform: "OpenAI",
    },
    {
      name: "gpt-4",
      label: "GPT-4",
      platform: "OpenAI",
    },
    {
      name: "sonar",
      label: "Sonar",
      platform: "Perplexity",
    },
  ];
  const modelsOptions = [
    {
      value: "default",
      label: (
        <div className="flex items-center gap-2 text-sm">
          <p>{t("By-default", "Par défaut")}</p>
        </div>
      ),
    },
    ...models?.map((model) => ({
      value: model.name,
      label: (
        <div className="flex items-center gap-2 text-sm">
          <p>
            {model.label} ({model.platform})
          </p>
        </div>
      ),
    })), // Fallback to an empty array if projects or data is undefined
  ];

  const handleGenerateCustom = async () => {
    trackPlatformUsed("custom", "ai_generation");
    try {
      let payload = {
        id:
          formikProps.values.id === null || formikProps.values.id === ""
            ? null
            : formikProps.values.id,
        style_id:
          formikProps.values.style_id === null ||
          formikProps.values.style_id === ""
            ? null
            : formikProps.values.style_id,
        company_id:
          formikProps.values.company_id === null ||
          formikProps.values.company_id === ""
            ? null
            : formikProps.values.company_id,
        product_id:
          formikProps.values.product_id === null ||
          formikProps.values.product_id === ""
            ? null
            : formikProps.values.product_id,
        prompt: prompt,
        ai_model: selectedModel,
        include_context: includeContext,
        include_gv: includeGV,
        websearch: websearch,
        subject: formikProps.values.seo_request,
        seo_request: formikProps.values.seo_request,
      };

      console.log(`custom generation payload: `, payload);

      const res = await generateCustom({
        ...payload,
        ...{ stream: true },
      }).unwrap();

      if (res?.status !== 200) {
        dispatch(setStreaming(false));
        toast.error(
          t(
            "something-went-wrong-with-generation",
            "Une erreur s'est produite lors de la génération"
          )
        );
        return;
      }
      dispatch(
        setStreaming({
          isStreaming: false,
        })
      );
      console.log(`custom generation res: `, res);

      editor
        .chain()
        .focus()
        .deleteRange({
          from: customData.startOfCustom,
          to: editor.state.selection.$from.end(2),
        })
        .insertContentAt(
          customData.startOfCustom,
          cleanGeneratedContent(res?.data)
        )
        .run();

      setOpenCustomModal(false);
      bufferRef.current = "";
    } catch (error) {
      handleGenerationError(error);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        //overflow: { md: 'unset', xs: 'scroll' },
        height: "100vh",
        width: {
          lg: "calc(100vw - 500px)",
          md: "calc(100vw - 350px)",
          xs: "100%",
        },
        display: "flex",
        mt: { md: "48px !important", xs: "unset" },
        left: 0,
        top: 0,
        bgcolor: "#000000a1",
        zIndex: 99999,
      }}
    >
      <div className="m-auto bg-white rounded-xl w-[80%] max-w-[500px]">
        <div className="flex justify-between p-4 items-center border-b-[3px] border-slate-200">
          <p>
            {t(
              "Generate-custom-instruction",
              "Générer un contenu personnalisé"
            )}
          </p>
          <Close
            onClick={() => setOpenCustomModal(false)}
            className="cursor-pointer"
            sx={{ fontSize: 32 }}
          />
        </div>
        <div className="p-4">
          {/* Customizing fields */}
          <div className="mb-4 flex items-center gap-4">
            {/* Left Column */}
            <div className="w-1/2">
              <div className="inline-flex items-center">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeGV}
                    onChange={(e) => setIncludeGV(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray_local-400 peer-checked:bg-blue-50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                </label>
                <span className="ms-3 text-sm">
                  Inclure les variables de génération
                </span>
              </div>

              <div className="inline-flex items-center mt-2">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeContext}
                    onChange={(e) => setIncludeContext(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray_local-400 peer-checked:bg-blue-50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                </label>
                <span className="ms-3 text-sm">Inclure le contexte</span>
              </div>

              {["gpt-4o"].includes(selectedModel) && (
                <div className="inline-flex items-center mt-2">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={websearch}
                      onChange={(e) => setWebsearch(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-gray_local-400 peer-checked:bg-blue-50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                  <span className="ms-3 text-sm">Rechercher sur le Web</span>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="w-1/2">
              <p className="mb-2">{t("ai-model", "Modèle d'IA")}</p>
              <ReactSelect
                options={modelsOptions}
                value={modelsOptions.find(
                  (option) => option.value === selectedModel
                )}
                onChange={(selectedOption) =>
                  setSelectedModel(selectedOption.value)
                }
                styles={{
                  menu: (base) => ({
                    ...base,
                    maxHeight: "464px",
                    overflowY: "auto",
                    zIndex: 9999,
                  }),
                }}
                menuPlacement="top"
                isSearchable={false}
              />
            </div>
          </div>

          <div className="mb-4">
            <p className="mb-2">
              {t("custom-instructions", "Instructions personnalisées")}
            </p>
            <TextField
              id="prompt"
              name="prompt"
              required={true}
              fullWidth
              onChange={(e) => setPrompt(e.target.value)}
              multiline={true}
              minRows={2}
              maxRows={18}
              placeholder={
                "Ex: " +
                t(
                  "placeholder-custom-instructions",
                  "Crée un tableau des meilleurs outils SEO ...'"
                )
              }
            />
          </div>

          <GenerationButton
            formikProps={formikProps}
            text={t(
              "Generate-custom-introduction",
              "Générer le contenu personnalisé"
            )}
            handleGenerate={handleGenerateCustom}
            isLoading={isLoading}
            platform={
              selectedModel === "default"
                ? "openai"
                : models
                    .find((model) => model.name === selectedModel)
                    .platform.toLowerCase()
            }
          />
        </div>
      </div>
    </Box>
  );
};

export default CustomModal;
